import {DateTime, Duration} from 'luxon';
import {connect} from 'react-redux';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {ResolveDispatchProps} from '../../../helpers/reduxHelpers';
import {SchedulingLinkModel} from '../../../models/api/schedulingLinkModel';
import {InterfaceEventsEnum, SchedulingSectionsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {AppState} from '../../../store/appState';
import {getCalendarWeekStartDay, getTimeFormat} from '../../../store/data/preferences/preferencesSelectors';
import {
  deleteSchedulingLinkAsyncAction,
  fetchSchedulingLinkAvailabilityAsyncAction,
} from '../../../store/data/schedulingLinks/schedulingLinksActions';
import {makeGetTimezone} from '../../../store/data/schedulingLinks/schedulingLinksSelectors';
import {MeetingTimesData} from '../../inboxes/composer/scheduling/meetingsettings/meetingSettingsPanel';
import {SchedulingLinksListItem} from './schedulingLinksListItem';

/*
 * Props.
 */

interface SchedulingLinksListItemContainerProps {
  schedulingLink: SchedulingLinkModel;
  onInsertMeetingTimes?: HandlerOf<MeetingTimesData>;
  isQuickInsert?: boolean;
}

/*
 * Selectors.
 */

function getIsQuickInsert(_state: AppState, props: SchedulingLinksListItemContainerProps) {
  return Boolean(props.isQuickInsert);
}

/*
 * Constants.
 */

const fetchAvailabilityDuration = Duration.fromObject({weeks: 2});

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SchedulingLinksListItemContainerProps>()(() => ({
  weekStart: getCalendarWeekStartDay,
  timezone: makeGetTimezone(),
  timeFormat: getTimeFormat,
  isQuickInsert: getIsQuickInsert,
}));

const mapDispatchToProps = makeMapDispatchToProps<SchedulingLinksListItemContainerProps>()(
  ({schedulingLink}) => ({
    fetchSchedulingLinkAvailabilityAsync: fetchSchedulingLinkAvailabilityAsyncAction,
    onDeleteClick: () => deleteSchedulingLinkAsyncAction(schedulingLink.id),
  }),
);

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: SchedulingLinksListItemContainerProps,
) {
  const {schedulingLink} = ownProps;
  const {timezone, timeFormat} = stateProps;
  const {fetchSchedulingLinkAvailabilityAsync} = dispatchProps;

  const createFetchAvailabilityAndInsertMeetingTimes =
    (onInsertMeetingTimes: HandlerOf<MeetingTimesData>) => async (): Promise<boolean> => {
      const nowDateTime = DateTime.local();
      const availability = await fetchSchedulingLinkAvailabilityAsync({
        linkId: schedulingLink.id,
        rangeStart: nowDateTime.toMillis(),
        rangeEnd: nowDateTime.plus(fetchAvailabilityDuration).toMillis(),
      });

      const hasAvailability = availability.timeSlots.length > 0;

      trackInterfaceEvent(InterfaceEventsEnum.SCHEDULING_COMPOSER_INSERT, {
        scheduling_link_id: schedulingLink.id,
        type: SchedulingSectionsEnum.MEETING_TIMES,
        has_availability: hasAvailability,
        is_quick_insert: ownProps.isQuickInsert,
      });

      if (!hasAvailability) {
        return false;
      }

      onInsertMeetingTimes({
        availability,
        linkId: schedulingLink.id,
        bookingUrl: schedulingLink.bookingPageUrl,
        timezone,
        timeFormat,
      });

      return true;
    };

  const fetchAvailabilityAndInsertMeetingTimesAsync = ownProps.onInsertMeetingTimes
    ? createFetchAvailabilityAndInsertMeetingTimes(ownProps.onInsertMeetingTimes)
    : undefined;

  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    fetchAvailabilityAndInsertMeetingTimesAsync,
  };
}

export const SchedulingLinksListItemContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(SchedulingLinksListItem);
